import { useMutation } from "@tanstack/react-query";
import { atom, useAtom, useAtomValue, useSetAtom, useStore } from "jotai";
import { CornerDownLeft } from "lucide-react";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { withGitpodSize } from "@/assets/icons/utils";
import type { PromptInputSubmission } from "@/components/ai/prompt-input/NewPromptInput";
import { Button } from "@/components/flexkit/Button";
import { PromptTemplate } from "@/components/flexkit/PromptTemplate";
import { useToast } from "@/components/flexkit/toast";
import { KeyboardCombo } from "@/components/keyboard/KeyboardCombo";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { useOnboardingCompletion } from "@/hooks/use-onboarding-completion";
import { PageCategories, usePageTracking } from "@/hooks/use-page-tracking";
import { useSegmentTrack } from "@/hooks/use-segment";
import { useOrganization } from "@/queries/organization-queries";
import {
    clearOnboardingStateFromLocalStorage,
    savePendingSubmissionToLocalStorage,
} from "@/routes/ona-onboarding/local-storage";
import {
    ONBOARDING_TASK_MODE,
    onboardingStateAtom,
    type TaskName,
    taskModeAtom,
    selectedTasksAtom,
    ONBOARDING_TASK_DESCRIPTIONS,
    promptInputSubmissionAtom,
    onboardingErrorsAtom,
} from "@/routes/ona-onboarding/onboarding-state";
import SkipOnboardingButton from "@/routes/ona-onboarding/SkipOnboardingButton";
import { StepHeadline } from "@/routes/ona-onboarding/StepHeadline";
import { StepSubheader } from "@/routes/ona-onboarding/StepSubheader";
import { formatError } from "@/utils/errors";
import type { Organization } from "gitpod-next-api/gitpod/v1/organization_pb";

// the custom prompt input box references this height as its height to prevent layout shift when switching between task modes
const predefinedTaskSelectorHeightAtom = atom(0);
const isPromptBoxFocusedAtom = atom(false);
const customTaskPromptAtom = atom(
    (get) => get(onboardingStateAtom).customTaskPrompt,
    (get, set, customTaskPrompt: string) => {
        set(onboardingStateAtom, { ...get(onboardingStateAtom), customTaskPrompt });
    },
);
const canSubmitAtom = atom((get) => get(customTaskPromptAtom).trim() !== "");

// Create a context to share the completion logic
const OnboardingCompletionContext = React.createContext<{
    handleComplete: () => void;
    isCompleting: boolean;
} | null>(null);

export function GetStartedWithTasks() {
    usePageTracking("Get Started with Tasks", PageCategories.OnaOnboarding);
    const { data: organization, isLoading: isLoadingOrganization, error: fetchOrganizationError } = useOrganization();
    const { markOnboardingAsComplete, isLoadingOnboardingCompletion } = useOnboardingCompletion();
    const promptInputSubmission = useAtomValue(promptInputSubmissionAtom);
    const navigate = useNavigate();
    const store = useStore();
    const { toast } = useToast();
    const [shouldContinue, setShouldContinue] = useState(false);

    const { mutate: completeOnboarding, isPending: isCompletingOnboarding } = useMutation({
        mutationFn: async ({
            submission,
            organization,
        }: {
            submission: PromptInputSubmission;
            organization: Organization;
        }) => {
            await markOnboardingAsComplete();
            savePendingSubmissionToLocalStorage(submission, organization.id);
            clearOnboardingStateFromLocalStorage();
        },
        onSuccess: () => {
            setShouldContinue(false);
            navigate("/ai", { replace: true, state: { submitPendingPrompt: true } });
        },
        onError: (error) => {
            toast({
                title: "Unable to continue with onboarding",
                description: formatError(error),
            });
        },
    });

    const handleComplete = useCallback(() => {
        const onboardingErrors = store.get(onboardingErrorsAtom);
        if (onboardingErrors && onboardingErrors.length > 0) {
            toast({
                title:
                    onboardingErrors.length > 1
                        ? "Unable to continue with onboarding due to several errors"
                        : "Unable to continue with onboarding due to an error",
                description: (
                    <ul className="list-disc">
                        {onboardingErrors.map((error, i) => (
                            <li key={i}>{formatError(error)}</li>
                        ))}
                    </ul>
                ),
            });
        } else if (fetchOrganizationError) {
            toast({
                title: "Unable to continue with onboarding",
                description: formatError(fetchOrganizationError),
            });
        } else if (promptInputSubmission.type === "success" && organization && !isLoadingOnboardingCompletion) {
            completeOnboarding({
                organization,
                submission: promptInputSubmission.data,
            });
        } else if (promptInputSubmission.type === "pending" || isLoadingOrganization) {
            setShouldContinue(true);
        }
    }, [
        store,
        toast,
        fetchOrganizationError,
        promptInputSubmission,
        organization,
        isLoadingOnboardingCompletion,
        completeOnboarding,
        isLoadingOrganization,
    ]);

    useEffect(() => {
        if (
            promptInputSubmission.type === "success" &&
            organization &&
            !isLoadingOnboardingCompletion &&
            shouldContinue
        ) {
            completeOnboarding({
                organization,
                submission: promptInputSubmission.data,
            });
        }
    }, [promptInputSubmission, organization, isLoadingOnboardingCompletion, shouldContinue, completeOnboarding]);

    return (
        <OnboardingCompletionContext.Provider value={{ handleComplete, isCompleting: isCompletingOnboarding }}>
            <div className="mx-0 mt-0 flex w-full max-w-4xl flex-col items-center justify-start p-8 md:m-auto md:justify-center">
                <StepHeadline className="mb-2">What do you want to get done today?</StepHeadline>
                <StepSubheader className="mb-8">
                    A team of AI software engineers will begin working on it immediately.
                </StepSubheader>
                <TaskCreatorComponent />
                <div className="mt-4 flex flex-row items-center justify-center gap-2">
                    <SkipOnboardingButton trackingId="skip-onboarding-get-started-with-tasks" />
                    <ContinueButton />
                </div>
            </div>
        </OnboardingCompletionContext.Provider>
    );
}

function TaskCreatorComponent() {
    const taskMode = useAtomValue(taskModeAtom);
    switch (taskMode) {
        case ONBOARDING_TASK_MODE.predefined:
            return <PredefinedTaskSelector />;
        case ONBOARDING_TASK_MODE.custom:
            return <CustomPromptBox />;
    }
}

function ContinueButton() {
    const taskMode = useAtomValue(taskModeAtom);
    const context = useContext(OnboardingCompletionContext);

    if (!context) {
        throw new Error("ContinueButton must be used within OnboardingCompletionContext");
    }

    return (
        <Button
            variant="primary"
            size="lg"
            className={cn("h-10 px-8 py-2", { hidden: taskMode === ONBOARDING_TASK_MODE.custom })}
            disabled={taskMode === ONBOARDING_TASK_MODE.custom || context.isCompleting}
            loading={context.isCompleting}
            onClick={context.handleComplete}
            data-tracking-id="continue-onboarding-get-started-with-tasks"
        >
            Continue
        </Button>
    );
}

function PredefinedTaskSelector() {
    const [selectedTasks, setSelectedTasks] = useAtom(selectedTasksAtom);
    const setTaskMode = useSetAtom(taskModeAtom);
    const setPredefinedTaskSelectorHeight = useSetAtom(predefinedTaskSelectorHeightAtom);
    const fieldSetRef = useRef<HTMLFieldSetElement>(null);

    useEffect(() => {
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                setPredefinedTaskSelectorHeight(entry.contentRect.height);
            }
        });
        const fieldSet = fieldSetRef.current;
        if (fieldSet) {
            observer.observe(fieldSet);
        }
        return () => {
            if (fieldSet) {
                observer.unobserve(fieldSet);
            }
        };
    }, [setPredefinedTaskSelectorHeight]);

    const selectTask = useCallback(
        (taskName: TaskName) => {
            // Radio button behavior: only one task selected at a time
            setSelectedTasks([taskName]);
        },
        [setSelectedTasks],
    );

    return (
        <>
            <fieldset name="selectedTask" className="flex w-full max-w-md flex-col space-y-3" ref={fieldSetRef}>
                {Object.values(ONBOARDING_TASK_DESCRIPTIONS).map((task) => (
                    <PromptTemplate
                        key={task.taskName}
                        className="w-full"
                        Icon={withGitpodSize(task.Icon)}
                        iconColor={task.iconColor}
                        iconBackgroundColor={task.iconBackgroundColor}
                        title={task.title}
                        description={task.description}
                        selected={selectedTasks.includes(task.taskName)}
                        size="base"
                        data-tracking-id={`select-task-${task.taskName}-get-started-with-tasks`}
                        onSelectChange={(selected) => {
                            if (selected) {
                                selectTask(task.taskName);
                            }
                            // When deselected, do nothing - radio button behavior
                        }}
                    />
                ))}
            </fieldset>
            <button
                type="submit"
                className="my-4 rounded-lg bg-transparent px-3 py-1.5 text-xs text-content-muted hover:bg-surface-hover"
                onClick={() => {
                    setTaskMode(ONBOARDING_TASK_MODE.custom);
                }}
                data-tracking-id="write-your-own-prompt-get-started-with-tasks"
            >
                or write your own prompt
            </button>
        </>
    );
}

function CustomPromptBox() {
    const setTaskMode = useSetAtom(taskModeAtom);
    return (
        <>
            <PromptBox />
            <button
                type="submit"
                className="my-4 rounded-lg bg-transparent px-4 py-2 text-content-primary hover:bg-surface-hover hover:bg-surface-secondary"
                onClick={() => {
                    setTaskMode(ONBOARDING_TASK_MODE.predefined);
                }}
                data-tracking-id="start-from-template-get-started-with-tasks"
            >
                or start from a template
            </button>
        </>
    );
}

function PromptBox() {
    const canSubmit = useAtomValue(canSubmitAtom);
    const predefinedTaskSelectorHeight = useAtomValue(predefinedTaskSelectorHeightAtom);
    const isFocused = useAtomValue(isPromptBoxFocusedAtom);
    const context = useContext(OnboardingCompletionContext);
    const prompt = useAtomValue(customTaskPromptAtom);
    const segmentTrack = useSegmentTrack();

    if (!context) {
        throw new Error("PromptBox must be used within OnboardingCompletionContext");
    }

    const handleSubmitClick = useCallback(() => {
        if (canSubmit && !context.isCompleting) {
            // Track prompt submission via button click
            segmentTrack("custom_prompt_used", {
                location: "onboarding",
                action: "submitting",
                prompt: prompt.slice(0, 500),
                prompt_length: prompt.length,
            });
            context.handleComplete();
        }
    }, [canSubmit, context, prompt, segmentTrack]);

    return (
        <div
            className={cn(
                "relative w-128 max-w-full overflow-clip rounded-xl border border-border-base bg-[var(--ona-white)] p-[1px] shadow-md outline-0 ring-0 dark:border-border-strong dark:bg-white/[3%]",
                {
                    "border-2 border-content-brand p-0 dark:border-content-brand": isFocused,
                    "h-32": predefinedTaskSelectorHeight <= 0,
                },
            )}
            style={predefinedTaskSelectorHeight > 0 ? { height: predefinedTaskSelectorHeight } : {}}
        >
            <PromptTextarea />
            <Tooltip
                content={
                    canSubmit ? (
                        <div className="flex flex-row items-center gap-2">
                            <span>Submit task</span>
                            <KeyboardCombo keys={["Enter"]} />
                        </div>
                    ) : (
                        "Enter a description of the task to continue"
                    )
                }
            >
                <button
                    type="button"
                    disabled={!canSubmit || context.isCompleting}
                    onClick={handleSubmitClick}
                    className={cn(
                        "absolute bottom-3 right-4 m-[1px] flex h-8 w-8 rounded-lg bg-content-primary p-2 text-white disabled:opacity-50 dark:text-black",
                        isFocused ? "m-0" : "m-[1px]",
                    )}
                    data-tracking-id="create-task-get-started-with-tasks"
                >
                    <CornerDownLeft size={16} />
                </button>
            </Tooltip>
        </div>
    );
}

function PromptTextarea() {
    const [prompt, setPrompt] = useAtom(customTaskPromptAtom);
    const setIsFocused = useSetAtom(isPromptBoxFocusedAtom);
    const canSubmit = useAtomValue(canSubmitAtom);
    const context = useContext(OnboardingCompletionContext);
    const segmentTrack = useSegmentTrack();
    const hasStartedTypingRef = useRef(false);

    if (!context) {
        throw new Error("PromptTextarea must be used within OnboardingCompletionContext");
    }

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
            // Check for plain Enter key
            if (e.key === "Enter" && canSubmit && !context.isCompleting) {
                e.preventDefault();
                // Track prompt submission
                segmentTrack("custom_prompt_used", {
                    location: "onboarding",
                    action: "submitting",
                    prompt: prompt.slice(0, 500),
                    prompt_length: prompt.length,
                });
                context.handleComplete();
            }
        },
        [canSubmit, prompt, segmentTrack, context],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const newValue = e.target.value;

            // Track when user starts typing (first character entered)
            if (!hasStartedTypingRef.current && newValue.length > 0 && prompt.length === 0) {
                hasStartedTypingRef.current = true;
                segmentTrack("custom_prompt_used", {
                    location: "onboarding",
                    action: "typing",
                });
            }

            setPrompt(newValue);
        },
        [prompt.length, segmentTrack, setPrompt],
    );

    return (
        <textarea
            onFocus={() => {
                setIsFocused(true);
            }}
            onBlur={() => {
                setIsFocused(false);
            }}
            onKeyDown={handleKeyDown}
            value={prompt}
            onChange={handleChange}
            className="h-full w-full resize-none bg-transparent px-4 pb-12 pt-3 outline-0 ring-0 placeholder:text-content-inactive focus:outline-0 focus:ring-0"
            placeholder="Describe the task in detail"
        />
    );
}
